import { existsSync, readFileSync } from "fs";
import { Hono } from "hono";
import { cors } from "hono/cors";

export interface ShutdownMarker {
  reason?: string;
  restartId?: string;
  at?: string;
}

export function readShutdownReasonFromMarker(
  markerPath = process.env.GENCOW_SHUTDOWN_MARKER_PATH,
): ShutdownMarker | null {
  if (!markerPath || !existsSync(markerPath)) return null;

  try {
    const raw = readFileSync(markerPath, "utf-8");
    const parsed = JSON.parse(raw) as ShutdownMarker | null;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function shouldFailFastOnBootError(params: { isBaas: boolean; isPlatform: boolean }): boolean {
  return params.isBaas || params.isPlatform;
}

export function buildFallbackErrorBody(error: unknown): {
  status: "error";
  error: string;
  hint: string;
  stack?: string;
} {
  const errorMsg = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  return {
    status: "error",
    error: errorMsg,
    hint: "Fix the error in your code. The server will auto-restart when you save.",
    stack: errorStack,
  };
}

export function formatBootFailureBanner(errorMessage: string, port: number): string {
  const RED = "\x1b[31m";
  const BOLD = "\x1b[1m";
  const RESET = "\x1b[0m";
  const YELLOW = "\x1b[33m";
  const width = 58;
  const pad = (value: string) => `  ${value.padEnd(width)}`;
  const line = "═".repeat(width + 2);

  return `
${RED}╔${line}╗${RESET}
${RED}║${pad(`${BOLD}❌ Server Boot Failed${RESET}${RED}`)}║${RESET}
${RED}║${pad("")}║${RESET}
${RED}║${pad(errorMessage.slice(0, width))}║${RESET}${
    errorMessage.length > width
      ? `
${RED}║${pad(errorMessage.slice(width, width * 2))}║${RESET}`
      : ""
  }
${RED}║${pad("")}║${RESET}
${YELLOW}║${pad("💡 Fix the error and save — server will auto-restart")}║${RESET}
${RED}║${pad(`   Listening on :${port} (error mode)`)}║${RESET}
${RED}╚${line}╝${RESET}
`;
}

export function startFallbackServer(params: {
  bootError: unknown;
  isBaas: boolean;
  isPlatform: boolean;
  port?: number;
}): void {
  const { bootError, isBaas, isPlatform } = params;

  if (isBaas) {
    console.error("[FATAL]", bootError);
    process.exit(1);
  }

  if (isPlatform) {
    console.error("[FATAL] Platform boot failed — exiting for systemd restart", bootError);
    process.exit(1);
  }

  const port = params.port ?? Number(process.env.PORT || 5456);
  const errorBody = buildFallbackErrorBody(bootError);
  console.error(formatBootFailureBanner(errorBody.error, port));

  const fallbackApp = new Hono();
  fallbackApp.use(
    "*",
    cors({
      origin: (origin) => {
        if (!origin || origin.startsWith("http://localhost")) return origin;
        return "";
      },
      credentials: true,
    }),
  );

  fallbackApp.get("/_admin/status", (c) =>
    c.json({
      ...errorBody,
      bootError: true,
    }),
  );
  fallbackApp.get("/", (c) => c.json(errorBody, 503));
  fallbackApp.all("*", (c) => c.json(errorBody, 503));

  try {
    Bun.serve({
      port,
      fetch: fallbackApp.fetch,
    });
    console.log(`[gencow] Fallback server listening on :${port} — waiting for file changes...`);
  } catch (serveError) {
    console.error("[gencow] Fallback server failed to start:", serveError);
  }
}
